import React, { useContext, useState } from 'react';
import { Pressable, Switch, Text, View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import { useParams } from 'react-router-dom';

import AuthContext from 'app/src/contexts/AuthContext';
import { Link } from 'app/src/utils/routing';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';

import { INVOICES_WITH_COUNT } from './queries';
import SubNav from './SubNav';
import Invoice from './show';
import EditInvoice from './edit';

export const baseColumns = [
  {
    key: 'id',
    label: 'Date',
    sort: 'desc',
    value: ({ entry }) => entry.bankDate,
    content: dateShort,
  },
  {
    key: 'reference',
    label: 'Invoice #',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'amount',
    label: 'Amount',
    content: ({ value }) => currency(value),
  },
  {
    key: 'dueOn',
    label: 'Due Date',
    content: dateShort,
  },
  {
    key: 'openBalance',
    label: 'Open Balance',
    content: ({ value }) => currency(value),
  },
  {
    key: 'lastSix',
    label: 'VIN',
    value: ({ entry }) => (
      entry.assets.map(asset => asset.lastSix).join(' ')
    ),
  },
  {
    key: 'saleOn',
    label: 'Sale',
    value: ({ entry }) => (
      entry.sales.map(sale => dateShort(sale.saleOn)).join(' ')
    ),
  },
  {
    key: 'offsettingTxns',
    label: 'Payment Dates',
    sort: 'prevent',
    content: ({ value }) => (
      <View style={{ flexDirection: 'row', gap: 10 }}>
        {value.map(paymentTxn => (
          <Link
            key={paymentTxn.id}
            to={`/accounting/payments/${paymentTxn.id}`}
          >
            <Text>{dateShort(paymentTxn.bankDate)}</Text>
          </Link>
        ))}
      </View>
    )
  },
];

const Invoices = () => {
  const { assetId } = useParams();
  const [showInvoiceModal, setShowInvoiceModal] = useState();
  const [showEditModal, setShowEditModal] = useState();
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const [scopes, setScopes] = useState(['invoice']);

  const queryVariables = {
    assetId,
    filterColumns: [],
    filterNames: [],
    scopes,
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const toggleScope = scope => {
    if (scopes.includes(scope)) {
      setScopes(scopes.filter(entry => entry !== scope));
    } else {
      setScopes([ ...scopes, scope ]);
    }
  };

  const { accounts, role, hasPolicy } = useContext(AuthContext);

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer style={{ gap: 20 }}>
          <Pressable onPress={() => setShowInvoiceModal(entry)}>
            <Text>View</Text>
          </Pressable>
          {hasPolicy({ type: 'Txn', action: 'update' }) && (
            <Pressable onPress={() => setShowEditModal(entry)}>
              <Text>Edit</Text>
            </Pressable>
          )}
        </LinkContainer>
      )
    },
  ];

  const buttons = (
    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
      <View style={{ flexDirection: 'row', gap: 10, marginRight: 20 }}>
        <Switch
          value={scopes.includes('open_balance')}
          onValueChange={() => toggleScope('open_balance')}
        />
        <Text style={{ fontSize: 18 }}>Show Unpaid Only?</Text>
      </View>

      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>
          <View style={{marginBottom: '20px'}}>
            <Filter.DateRange
              columnKey="bank_date"
              label="Date"
              filters={filters}
              setFilter={setFilter}
            />
          </View>
          <Text>Account:</Text>
          <select
            type={'select'}
            name="account_id"
            onChange={e => {
              setFilter('account_id', e.target.value)}
            }
            value={filters.account_id || ''}
          >
            <option key={`account-any`}>{'Any'}</option>
            {(accounts || []).map(account => (
              <option key={account.id} value={account.id}>
                {account.label}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      {!assetId &&
        <SubNav refetch={refetch} />
      }

      <ContentContainer>
        <PageTitle>Invoices</PageTitle>

        <DataTable.Gql
          setRefetch={setRefetch}
          query={INVOICES_WITH_COUNT}
          queryName='txnsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.txnsWithCount)}
          htmlTable
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Invoice"
        >
          <EditInvoice
            txn={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
          />
        </Modal>
      }
      {showInvoiceModal &&
        <Modal
          showModal={showInvoiceModal}
          setShowModal={setShowInvoiceModal}
          modalType="Invoice Detail"
          objectName={showInvoiceModal.invoiceNumber}
        >
          <Invoice
            id={showInvoiceModal.id}
            setShowModal={setShowInvoiceModal}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default Invoices;
